import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { Linking } from 'react-native'
import { Trans } from 'react-i18next'
import styled from 'styled-components/native'

import I18n from 'APP/I18n'
import { selectPatientProfile } from 'APP/Redux/PatientRedux'
import { useSelector } from 'APP/Hooks/redux'
import Analytics from 'APP/Services/Analytics'
import { logger } from 'APP/Lib/Datadog'

import Button from 'APP/Converse/Button'
import { ResponsiveView } from 'APP/Converse/Layout'
import Typography from 'APP/Converse/Typography'
import {
  AppointmentCheckoutItem,
  AppointmentCheckoutItemProps,
} from './CheckoutItem/AppointmentCheckoutItem'
import { ServiceCheckoutItem, ServiceCheckoutItemProps } from './CheckoutItem/ServiceCheckoutItem'
import { PaymentTerms } from './PaymentTerms'
import PaymentMethods from './PaymentMethods'
import TotalsSection from './TotalsSection'

export enum CHECKOUT_TYPE {
  APPOINTMENT = 'appointment',
  SERVICE = 'service',
}

export interface FeePolicy {
  encounterFeeAmount: number
  memberLateCancellationAmount?: number
  memberNoShowAmount?: number
}

export interface BaseCheckoutContext {
  tos?: string
}

export type CheckoutContext<T extends CHECKOUT_TYPE> = T extends CHECKOUT_TYPE.APPOINTMENT
  ? BaseCheckoutContext & AppointmentCheckoutItemProps
  : T extends CHECKOUT_TYPE.SERVICE
  ? BaseCheckoutContext & ServiceCheckoutItemProps
  : never

export type CheckoutProps =
  | {
      type: CHECKOUT_TYPE.APPOINTMENT
      feePolicy: FeePolicy
      context: CheckoutContext<CHECKOUT_TYPE.APPOINTMENT>
      onConfirm: (ctx: CheckoutContext<CHECKOUT_TYPE.APPOINTMENT>) => Promise<void>
    }
  | {
      type: CHECKOUT_TYPE.SERVICE
      feePolicy: FeePolicy
      context: CheckoutContext<CHECKOUT_TYPE.SERVICE>
      onConfirm: (ctx: CheckoutContext<CHECKOUT_TYPE.SERVICE>) => Promise<void>
    }

const Container = styled(ResponsiveView)`
  flex: 1;
  gap: ${({ theme }) => theme.metrics.baseMargin * 2}px;
`

const ContentContainer = styled(ResponsiveView)`
  gap: ${({ theme }) => theme.metrics.baseMargin * 2}px;
`

const ButtonContainer = styled.View`
  padding-vertical: ${({ theme }) => theme.metrics.baseMargin}px;
  gap: ${({ theme }) => theme.metrics.baseMargin / 2}px;
  align-items: center;
`

const ServiceCheckoutItemsContainer = styled.View`
  gap: ${({ theme }) => theme.metrics.baseMargin / 2}px;
`

export const Checkout = ({ feePolicy, type: checkoutType, context, onConfirm }: CheckoutProps) => {
  const [isLocalLoading, setIsLocalLoading] = useState(false)
  const { creditCard: creditCardDetails } = useSelector(selectPatientProfile)
  const supportedPaymentMethods = useMemo(
    () => (creditCardDetails ? [creditCardDetails] : []),
    [creditCardDetails]
  )

  const handleToSPress = useCallback(() => {
    Linking.openURL(context.tos!).catch((err) => {
      logger.error('Could not open ToS for Checkout:', err?.stack || String(err))
    })
  }, [context?.tos])

  const handleConfirm = useCallback(async () => {
    setIsLocalLoading(true)

    try {
      await onConfirm(
        context as unknown as CheckoutContext<CHECKOUT_TYPE.SERVICE> &
          CheckoutContext<CHECKOUT_TYPE.APPOINTMENT>
      )

      // TODO: Add logic to perform charge here instead of relying on consumers to perform this logic via their onConfirm calls,  See https://dialoguemd.atlassian.net/browse/CLI-875

      Analytics.trackEvent('button_click', {
        button_value: `Completed Checkout`,
      })
    } catch (_error) {
      setIsLocalLoading(false)
    }
  }, [onConfirm, context])

  useEffect(() => {
    Analytics.trackScreen('Checkout')
  }, [checkoutType])

  return (
    <Container testID="CHECKOUT_CONTAINER">
      <ContentContainer>
        {checkoutType === CHECKOUT_TYPE.APPOINTMENT && <AppointmentCheckoutItem {...context} />}

        {checkoutType === CHECKOUT_TYPE.SERVICE && (
          <ServiceCheckoutItemsContainer>
            <Typography variant="bodyLargeBold">{I18n.t('Checkout.whatsIncluded')}:</Typography>
            <ServiceCheckoutItem {...context} />
          </ServiceCheckoutItemsContainer>
        )}

        <TotalsSection totalAmount={feePolicy.encounterFeeAmount} />

        <PaymentMethods methods={supportedPaymentMethods} />

        <PaymentTerms
          checkoutType={checkoutType}
          termsContext={{
            lateCancellationAmount: feePolicy.memberLateCancellationAmount,
            noShowAmount: feePolicy.memberNoShowAmount,
          }}
        />
      </ContentContainer>

      <ButtonContainer>
        <Typography align="center" variant="bodySmall">
          <Trans
            i18n={I18n}
            i18nKey={
              context?.tos
                ? 'Checkout.agreementNoticeWithToS'
                : 'Checkout.agreementNoticeWithoutToS'
            }
            values={context?.tos}
            components={[<Typography variant="linkSmall" key="link" onPress={handleToSPress} />]}
          />
        </Typography>
        <Button
          variant="primary"
          widthVariant="full"
          title={
            checkoutType === CHECKOUT_TYPE.APPOINTMENT
              ? I18n.t('Checkout.confirmAndBook')
              : I18n.t('Checkout.confirmAndPay')
          }
          onPress={handleConfirm}
          disabled={supportedPaymentMethods.length === 0 || isLocalLoading}
          isLoading={isLocalLoading}
          testID="CHECKOUT_BTN"
        />
      </ButtonContainer>
    </Container>
  )
}

export default Checkout
